import { useCallback, useEffect, useState } from 'react';
import type { GameMode, SessionSummary, StatsView } from '@tm/shared';
import { api, type Me, type StartedSession } from './lib/api.js';
import { hideBackButton, showBackButton } from './lib/telegram.js';
import { trackGoal, trackScreen } from './lib/metrika.js';
import { Home } from './screens/Home.js';
import { Game } from './screens/Game.js';
import { Result } from './screens/Result.js';
import { Stats } from './screens/Stats.js';
import { Achievements } from './screens/Achievements.js';
import { Duels } from './screens/Duels.js';
import { Profile } from './screens/Profile.js';

type Tab = 'home' | 'stats' | 'ach' | 'duels' | 'profile';
type View = { kind: 'tabs' } | { kind: 'game'; session: StartedSession } | { kind: 'result'; summary: SessionSummary };

const TABS: { id: Tab; icon: string; label: string }[] = [
  { id: 'home', icon: '🏠', label: 'Играть' },
  { id: 'stats', icon: '📈', label: 'Прогресс' },
  { id: 'duels', icon: '⚔️', label: 'Дуэли' },
  { id: 'ach', icon: '🏅', label: 'Награды' },
  { id: 'profile', icon: '⚙️', label: 'Настройки' },
];

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [stats, setStats] = useState<StatsView | null>(null);
  const [tab, setTab] = useState<Tab>('home');
  const [view, setView] = useState<View>({ kind: 'tabs' });
  const [error, setError] = useState<string | null>(null);
  const [lastStart, setLastStart] = useState<{ mode: GameMode; table?: number; duelId?: string } | null>(null);

  const loadMe = useCallback(async () => {
    try {
      const data = await api.me();
      setMe(data);
      // Пришли по ссылке-вызову — открываем сразу дуэли, чтобы не искать вкладку.
      if (data.joinedDuel) setTab('duels');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить данные');
    }
  }, []);

  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  // Статистика тяжелее остальных запросов — тянем её только когда открыли вкладку.
  useEffect(() => {
    if ((tab === 'stats' || tab === 'ach') && view.kind === 'tabs') {
      api.stats().then(setStats).catch(() => setStats(null));
    }
  }, [tab, view.kind]);

  const play = useCallback(async (mode: GameMode, opts: { table?: number; duelId?: string } = {}) => {
    setError(null);
    setLastStart({ mode, ...opts });
    try {
      setView({ kind: 'game', session: await api.startSession(mode, opts) });
      trackGoal('game_start', { mode });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось начать игру');
    }
  }, []);

  const toTabs = useCallback(() => {
    setView({ kind: 'tabs' });
    void loadMe();
    setStats(null);
  }, [loadMe]);

  // Приложение одностраничное, поэтому просмотры экранов отправляем сами.
  useEffect(() => {
    trackScreen(view.kind === 'tabs' ? tab : view.kind);
  }, [tab, view.kind]);

  // Системная кнопка «назад» Telegram вместо своей — так привычнее внутри мессенджера.
  useEffect(() => {
    if (view.kind === 'tabs') {
      hideBackButton(toTabs);
      return;
    }
    showBackButton(toTabs);
    return () => hideBackButton(toTabs);
  }, [view.kind, toTabs]);

  if (error && !me) {
    return (
      <div className="empty">
        <p>{error}</p>
        <p className="small">Приложение нужно открывать из Telegram — так бот понимает, кто играет.</p>
      </div>
    );
  }

  if (!me) return <div className="empty">Загружаю…</div>;

  if (view.kind === 'game') {
    return (
      <Game
        session={view.session}
        onDone={(summary) => {
          trackGoal('game_finish', {
            mode: summary.mode,
            correct: summary.correct,
            total: summary.total,
            accuracy: Math.round(summary.accuracy * 100),
          });
          setView({ kind: 'result', summary });
        }}
        onExit={toTabs}
      />
    );
  }

  if (view.kind === 'result') {
    return (
      <Result
        summary={view.summary}
        onAgain={() => {
          if (lastStart && lastStart.mode !== 'duel') void play(lastStart.mode, lastStart);
          else toTabs();
        }}
        onHome={toTabs}
      />
    );
  }

  return (
    <>
      {error && <div className="card small" style={{ color: 'var(--critical)' }}>{error}</div>}

      {tab === 'home' && <Home me={me} onPlay={play} onDuels={() => setTab('duels')} />}
      {tab === 'stats' && (stats ? <Stats stats={stats} /> : <div className="empty">Считаю…</div>)}
      {tab === 'ach' && (stats ? <Achievements stats={stats} /> : <div className="empty">Считаю…</div>)}
      {tab === 'duels' && (
        <Duels
          duels={me.duels}
          meId={me.user.id}
          onPlay={(duelId) => void play('duel', { duelId })}
          onRefresh={() => void loadMe()}
        />
      )}
      {tab === 'profile' && <Profile me={me} onSaved={loadMe} />}

      <nav className="tabbar">
        {TABS.map((t) => (
          <button key={t.id} className={tab === t.id ? 'on' : undefined} onClick={() => setTab(t.id)}>
            <span className="ico">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </nav>
    </>
  );
}
